
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Layers, Phone, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const RoofingMaterialsFAQPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Roofing Materials FAQ: Best Roof Types for North Carolina | Carolina Trophy Roofs</title>
        <meta name="description" content="Compare roofing materials, learn about durability, costs, and which roof type is best for North Carolina weather. Expert material guidance from Carolina Trophy Roofs." />
        <meta name="keywords" content="roofing materials FAQ, best roof materials NC, asphalt shingles, metal roofing, tile roofing, roofing material comparison" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/roofing-materials-faq" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Roofing Materials FAQ</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Layers className="mr-2 h-4 w-4" />
              Materials & Products
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Roofing Materials FAQ: Choosing the Right Roof for Your Home
            </h1>
            <p className="text-xl text-gray-600">
              Expert guidance on roofing materials, performance, and suitability for North Carolina
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {/* Asphalt Shingles */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Asphalt Shingles Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">What are the different types of asphalt shingles?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Asphalt Roofing Manufacturers Association (ARMA), there are three main categories:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>3-Tab Shingles:</strong> Basic, uniform appearance, 20-25 year lifespan</li>
                    <li><strong>Architectural/Dimensional:</strong> Textured, varied appearance, 25-30 year lifespan</li>
                    <li><strong>Luxury/Premium:</strong> High-end appearance, 30-50 year lifespan, enhanced features</li>
                  </ul>
                  <p className="text-gray-700">
                    The National Association of Home Builders reports that architectural shingles now represent 
                    over 80% of the residential roofing market due to their superior durability and aesthetics.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">How do asphalt shingles perform in North Carolina weather?</h3>
                  <p className="text-gray-700 mb-3">
                    According to testing by Underwriters Laboratories (UL) and the International Code Council (ICC), 
                    asphalt shingles perform well in North Carolina's climate when properly installed:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Wind Resistance:</strong> Class A shingles rated for 110+ mph winds</li>
                    <li><strong>Heat Performance:</strong> Modern shingles designed for temperatures up to 160°F</li>
                    <li><strong>Moisture Resistance:</strong> Granule coatings provide UV and water protection</li>
                    <li><strong>Impact Resistance:</strong> Class 4 shingles available for hail-prone areas</li>
                  </ul>
                  <p className="text-gray-700">
                    The Insurance Institute for Business & Home Safety (IBHS) testing shows that properly installed 
                    architectural shingles can withstand hurricane-force winds common in coastal North Carolina.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What is the difference between organic and fiberglass shingles?</h3>
                  <p className="text-gray-700 mb-3">
                    According to manufacturer specifications and ARMA data:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Fiberglass Base (Most Common):</strong> Better fire resistance, lighter weight, longer lifespan</li>
                    <li><strong>Organic Base (Less Common):</strong> Better flexibility in cold weather, heavier</li>
                  </ul>
                  <p className="text-gray-700">
                    Consumer Reports testing shows fiberglass shingles perform better in hot climates like North Carolina, 
                    with superior fire ratings and resistance to thermal cycling damage.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Do asphalt shingles qualify for energy efficiency rebates?</h3>
                  <p className="text-gray-700 mb-3">
                    According to Energy Star and the Cool Roof Rating Council, certain asphalt shingles with 
                    high solar reflectance qualify for utility rebates:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Energy Star certified cool shingles reflect 25% more solar energy</li>
                    <li>Can reduce cooling costs by 7-15% according to Lawrence Berkeley National Laboratory</li>
                    <li>Some NC utilities offer rebates for cool roof installations</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Metal Roofing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Shield className="mr-2 h-6 w-6" />
                  Metal Roofing Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Is metal roofing suitable for North Carolina homes?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Metal Roofing Alliance and Florida Solar Energy Center research, 
                    metal roofing offers significant advantages for North Carolina's climate:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Hurricane Resistance:</strong> Tested to withstand 180+ mph winds</li>
                    <li><strong>Energy Efficiency:</strong> Reflects 70% of solar energy, reducing cooling costs 10-25%</li>
                    <li><strong>Longevity:</strong> 40-70 year lifespan, often outlasting the structure</li>
                    <li><strong>Fire Resistance:</strong> Class A fire rating, non-combustible</li>
                  </ul>
                  <p className="text-gray-700">
                    The National Weather Service data shows metal roofing performs exceptionally well 
                    in coastal Carolina's salt air and high humidity conditions.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What types of metal roofing are available?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Metal Construction Association and manufacturer specifications:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Standing Seam:</strong> Most popular, sleek appearance, excellent weather protection</li>
                    <li><strong>Metal Shingles:</strong> Traditional shingle appearance with metal benefits</li>
                    <li><strong>Corrugated/Ribbed:</strong> Agricultural/industrial look, very cost-effective</li>
                    <li><strong>Stone-Coated Steel:</strong> Combines metal durability with traditional aesthetics</li>
                  </ul>
                  <p className="text-gray-700">
                    Materials include aluminum (best for coastal areas), steel (most common), copper (premium), 
                    and zinc (eco-friendly option).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Are metal roofs noisy during rain and hail?</h3>
                  <p className="text-gray-700 mb-3">
                    According to acoustic testing by the Acoustic Society of America and metal roofing manufacturers:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Properly installed metal roofs with solid decking are only 6 decibels louder than asphalt</li>
                    <li>Sound difference is negligible inside the home with proper insulation</li>
                    <li>Stone-coated metal panels reduce noise even further</li>
                  </ul>
                  <p className="text-gray-700">
                    Modern installation techniques with solid sheathing and insulation effectively eliminate 
                    the noise concerns associated with older metal roofing installations.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Do metal roofs attract lightning?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Lightning Protection Institute and National Weather Service:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Metal roofs do NOT attract lightning more than other materials</li>
                    <li>Lightning strikes the highest point in an area, regardless of material</li>
                    <li>If struck, metal roofs actually provide better protection by dispersing energy</li>
                    <li>Metal is non-combustible, reducing fire risk from lightning strikes</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Alternative Materials */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Alternative Roofing Materials</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">How do tile roofs perform in North Carolina?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Tile Roofing Institute and hurricane impact testing:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Durability:</strong> Clay tiles can last 50-100+ years</li>
                    <li><strong>Wind Resistance:</strong> Properly installed tiles withstand 125+ mph winds</li>
                    <li><strong>Energy Efficiency:</strong> Natural thermal properties reduce cooling costs</li>
                    <li><strong>Weight Considerations:</strong> Require structural assessment for older homes</li>
                  </ul>
                  <p className="text-gray-700">
                    The Florida Building Code research shows tile roofing performs well in hurricane conditions, 
                    making it suitable for coastal North Carolina applications.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What about synthetic roofing materials?</h3>
                  <p className="text-gray-700 mb-3">
                    According to manufacturer testing and the Synthetic Roofing Products Association:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Composite Shingles:</strong> Mimic slate/wood appearance, 30-50 year lifespan</li>
                    <li><strong>Rubber Roofing:</strong> Excellent for low-slope applications, 20-30 years</li>
                    <li><strong>Synthetic Slate:</strong> Lighter than natural slate, similar appearance</li>
                    <li><strong>Recycled Materials:</strong> Eco-friendly options using recycled plastics</li>
                  </ul>
                  <p className="text-gray-700">
                    Many synthetic materials offer Class A fire ratings and impact resistance suitable 
                    for North Carolina's weather challenges.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Are solar roofing options viable in North Carolina?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the National Renewable Energy Laboratory (NREL) and Solar Power World data:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Solar Potential:</strong> NC receives 4.2-4.7 peak sun hours daily</li>
                    <li><strong>Solar Shingles:</strong> Tesla and GAF offer integrated solar roofing systems</li>
                    <li><strong>Incentives:</strong> 30% federal tax credit plus NC state incentives</li>
                    <li><strong>ROI:</strong> Typical payback period of 8-12 years in North Carolina</li>
                  </ul>
                  <p className="text-gray-700">
                    The Database of State Incentives for Renewables & Efficiency shows North Carolina 
                    offers some of the best solar incentive programs in the Southeast.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What roofing material is most environmentally friendly?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Green Building Council and Environmental Protection Agency analysis:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Metal Roofing:</strong> 25-95% recycled content, 100% recyclable</li>
                    <li><strong>Clay/Concrete Tiles:</strong> Natural materials, very long lifespan</li>
                    <li><strong>Recycled Synthetic:</strong> Made from post-consumer materials</li>
                    <li><strong>Cool Roofs:</strong> Reduce urban heat island effect and energy use</li>
                  </ul>
                  <p className="text-gray-700">
                    Life cycle assessments show that longer-lasting materials like metal and tile 
                    have lower environmental impact over time despite higher initial energy costs.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Performance and Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Zap className="mr-2 h-6 w-6" />
                  Performance Comparisons
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Which roofing material offers the best value?</h3>
                  <p className="text-gray-700 mb-3">
                    According to cost-per-year analysis from Remodeling Magazine and Consumer Reports:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 mb-3">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 p-2 text-left">Material</th>
                          <th className="border border-gray-300 p-2 text-left">Initial Cost</th>
                          <th className="border border-gray-300 p-2 text-left">Lifespan</th>
                          <th className="border border-gray-300 p-2 text-left">Cost/Year</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 p-2">Asphalt (Basic)</td>
                          <td className="border border-gray-300 p-2">$8,000-$12,000</td>
                          <td className="border border-gray-300 p-2">20-25 years</td>
                          <td className="border border-gray-300 p-2">$320-$600</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-2">Asphalt (Premium)</td>
                          <td className="border border-gray-300 p-2">$12,000-$18,000</td>
                          <td className="border border-gray-300 p-2">30-40 years</td>
                          <td className="border border-gray-300 p-2">$300-$600</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 p-2">Metal</td>
                          <td className="border border-gray-300 p-2">$15,000-$25,000</td>
                          <td className="border border-gray-300 p-2">50-70 years</td>
                          <td className="border border-gray-300 p-2">$215-$500</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 p-2">Tile</td>
                          <td className="border border-gray-300 p-2">$18,000-$30,000</td>
                          <td className="border border-gray-300 p-2">50-100 years</td>
                          <td className="border border-gray-300 p-2">$180-$600</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-gray-700">
                    When factoring in energy savings, insurance discounts, and resale value, 
                    premium materials often provide superior long-term value.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What roofing material is best for hurricane resistance?</h3>
                  <p className="text-gray-700 mb-3">
                    According to IBHS testing and Florida International University hurricane research:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Metal Roofing:</strong> Best overall performance, tested to 180+ mph</li>
                    <li><strong>Impact-Resistant Asphalt:</strong> Class 4 shingles, 130+ mph ratings</li>
                    <li><strong>Concrete Tile:</strong> Excellent when properly attached, 125+ mph</li>
                    <li><strong>Modified Bitumen:</strong> Good for low-slope applications</li>
                  </ul>
                  <p className="text-gray-700">
                    Proper installation is crucial - even the best materials fail without correct 
                    fastening patterns and wind-resistant installation techniques.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">How do different materials affect home insurance costs?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Insurance Information Institute and major insurance carriers:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Impact-Resistant Materials:</strong> 10-35% premium discounts available</li>
                    <li><strong>Metal Roofing:</strong> Up to 20% discounts from some insurers</li>
                    <li><strong>Class A Fire Rating:</strong> Required in some areas, may reduce premiums</li>
                    <li><strong>Wind-Resistant Installation:</strong> Additional discounts for certified installation</li>
                  </ul>
                  <p className="text-gray-700">
                    Discounts vary by insurer and location, but can result in hundreds of dollars 
                    in annual savings for hurricane-rated materials.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Authoritative Sources */}
          <Card className="mt-12">
            <CardHeader>
              <CardTitle>Authoritative Sources & References</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Industry Organizations:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.asphaltroofing.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Asphalt Roofing Manufacturers Association (ARMA)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.metalroofing.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Metal Roofing Alliance
                      </a>
                    </li>
                    <li>
                      <a href="https://www.tileroofing.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Tile Roofing Institute
                      </a>
                    </li>
                    <li>
                      <a href="https://www.coolroofs.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Cool Roof Rating Council
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Testing & Research:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.ul.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Underwriters Laboratories (UL)
                      </a>
                    </li>
                    <li>
                      <a href="https://www.disastersafety.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Insurance Institute for Business & Home Safety
                      </a>
                    </li>
                    <li>
                      <a href="https://www.nrel.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        National Renewable Energy Laboratory
                      </a>
                    </li>
                    <li>
                      <a href="https://www.consumerreports.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Consumer Reports
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <h2 className="text-2xl font-bold mb-4">Need Help Choosing the Right Material?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Our experts can help you select the perfect roofing material for your home and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <a href="tel:252-371-2266">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 371-2266
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Get Material Recommendations</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RoofingMaterialsFAQPage;
